import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { useApi } from '../../hooks/useApi.js';
import { api, ApiError } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';
import { SLATE, formatTime } from '../shifts/shifts-shared.js';
import { parseUtc } from '../../lib/shop-time.js';
import { ClockInEditor } from '../shifts/ClockInEditor.jsx';

// No "expected shift length" concept exists in the schema — a fixed 8h
// assumption, per El, rather than a new per-shift/per-user setting.
const SHIFT_LENGTH_MS = 8 * 60 * 60 * 1000;

const formatCountdown = (ms) => {
  const over = ms < 0;
  const totalSeconds = Math.floor(Math.abs(ms) / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return { over, text: `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s` };
};

export const ShiftWidget = () => {
  const { route } = useLocation();
  const { data, refetch } = useApi('/shifts/status');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!data?.clockedIn) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [data?.clockedIn]);

  const countdown = data?.clockedIn && data.shift?.clockInAt
    ? formatCountdown(SHIFT_LENGTH_MS - (now.getTime() - parseUtc(data.shift.clockInAt).getTime()))
    : null;

  const toggle = async () => {
    setBusy(true); setError(null);
    try {
      if (data?.clockedIn) await api.post('/shifts/clock-out');
      else await api.post('/shifts/clock-in');
      refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="rounded-md border border-border-subtle bg-bg-card p-4">
      <div class="mb-3 flex items-center justify-between">
        <span class="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: SLATE }}>
          <span class="block h-2 w-2 rounded-full" style={{ background: SLATE }} />
          Shift Tracker
        </span>
        <button type="button" onClick={() => route('/shifts')} class="text-xs text-text-muted hover:text-brand">
          View shift log →
        </button>
      </div>

      <div class="flex items-center justify-between gap-4">
        <div>
          {data?.clockedIn ? (
            <div>
              <div class="flex items-center gap-2">
                <p class="m-0 text-sm text-text-secondary">
                  Clocked in at <span class="font-medium text-text-primary">{formatTime(data.shift?.clockInAt)}</span>
                </p>
                <ClockInEditor clockInAt={data.shift?.clockInAt} onSaved={refetch} />
              </div>
              {countdown && (
                <p class={`m-0 mt-1 font-mono text-[13px] font-semibold ${countdown.over ? 'text-ink-magenta' : 'text-text-primary'}`}>
                  {countdown.text} {countdown.over ? 'over' : 'left'}
                </p>
              )}
            </div>
          ) : (
            <p class="m-0 text-sm text-text-secondary">Not clocked in.</p>
          )}
          {error && <p class="m-0 mt-1 text-[12px] font-semibold text-ink-magenta">{error}</p>}
        </div>
        <Button variant="primary" onClick={toggle} disabled={busy || !data}>
          {busy ? 'Working…' : data?.clockedIn ? 'Clock out' : 'Clock in'}
        </Button>
      </div>

      {data?.team && (
        <div class="mt-3 border-t border-border-subtle pt-3">
          <p class="m-0 mb-1.5 text-[11px] uppercase tracking-label text-text-muted">Currently clocked in</p>
          {data.team.length === 0 ? (
            <p class="m-0 text-[12px] text-text-tertiary">Nobody right now.</p>
          ) : (
            <ul class="m-0 flex flex-wrap gap-x-4 gap-y-1 p-0 text-[12px] text-text-body">
              {data.team.map((s) => (
                <li key={s.id} class="list-none">
                  {s.userName ?? 'Unattributed'} <span class="text-text-muted">· since {formatTime(s.clockInAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
